import React, { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Sector, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function VehicleUtilizationChart({ data, city, cities }) {
  const [activeTimeframe, setActiveTimeframe] = useState("7 Days");
  const [chartType, setChartType] = useState("breakdown"); // "breakdown" or "utilization"
  // Tracks which slice ID is currently active/expanded
  const [expandedSliceId, setExpandedSliceId] = useState(null);
  // Tracks which legends are active/visible
  const [activeLegends, setActiveLegends] = useState({
    revenue: true,
    fuelCost: true,
    maintenance: true,
    netProfit: true,
  });

  // 1. Process your backend analytics payload safely
  const analytics = data || {};
  const costing = analytics?.costingAndProfitability || {};
  const rawCosts = costing?.costs || {};

  const revenue = Math.max(0, Number(costing?.revenue || 0));
  const fuelCost = Math.max(0, Number(rawCosts?.fuel || 0));
  const maintenance = Math.max(0, Number(rawCosts?.maintenance || 0));
  const netProfit = Math.max(0, Number(costing?.netProfit || 0));
  
  // Use breakEvenDays directly from the API response
  const breakEvenDays = costing?.breakEvenDays || 0;

  const numericalTotal = revenue + fuelCost + maintenance + netProfit;

  // 2. Compute true percentages
  const revenuePct = numericalTotal > 0 ? Math.round((revenue / numericalTotal) * 100) : 0;
  const fuelPct = numericalTotal > 0 ? Math.round((fuelCost / numericalTotal) * 100) : 0;
  const maintPct = numericalTotal > 0 ? Math.round((maintenance / numericalTotal) * 100) : 0;
  const profitPct = numericalTotal > 0 ? Math.round((netProfit / numericalTotal) * 100) : 0;

  const activeUtilizationRate = analytics?.utilizationRate || "0.00";
  const timeframes = ["7 Days", "2 Weeks", "1 Month", "3 Months"];

  // 3. Build data for breakdown chart
  const breakdownData = [
    { name: "Revenue", value: revenue, color: "#6559BC", isPositive: true },
    { name: "Fuel Cost", value: -fuelCost, color: "#DC2626", isPositive: false, absoluteValue: fuelCost },
    { name: "Maintenance", value: -maintenance, color: "#22C55E", isPositive: false, absoluteValue: maintenance },
    { name: "Net Profit", value: netProfit, color: "#92400E", isPositive: true }
  ];

  // 4. Build data matrix structure for Recharts (Utilization Chart)
  const allChartData = [
    { id: 0, value: revenuePct, name: "Revenue", color: "#6559BC", key: "revenue" },
    { id: 1, value: fuelPct, name: "Fuel Cost", color: "#DC2626", key: "fuelCost" },
    { id: 2, value: maintPct, name: "Maintenance", color: "#22C55E", key: "maintenance" },
    { id: 3, value: profitPct, name: "Net Profit", color: "#92400E", key: "netProfit" }
  ];

  const chartData = allChartData.filter(item => activeLegends[item.key]);
  const hasAnyActive = Object.values(activeLegends).some(v => v === true);
  const showGreyRing = !hasAnyActive || numericalTotal === 0;

  const selectedItem = expandedSliceId !== null && !showGreyRing 
    ? allChartData.find(c => c.id === expandedSliceId) 
    : null;
  const selectedPercentage = selectedItem ? selectedItem.value : null;

  // Custom Tooltip for Breakdown Chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const absValue = Math.abs(value);
      const isNegative = value < 0;
      return (
        <div className="bg-white border border-stone-200 rounded-lg p-2 shadow-lg">
          <p className="text-stone-900 text-xs font-semibold">{payload[0].name}</p>
          <p className={`text-sm font-bold ${isNegative ? 'text-red-500' : 'text-green-500'}`}>
            {isNegative ? '- ' : ''}₹{absValue.toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
    return (
      <g>
        <Sector
          cx={cx + 2}
          cy={cy + 2}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 10}
          startAngle={startAngle}
          endAngle={endAngle}
          fill="#00000020"
          cornerRadius={8}
        />
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 10}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          cornerRadius={8}
          stroke={`${fill}80`}
          strokeWidth={2}
        />
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={outerRadius + 8}
          outerRadius={outerRadius + 14}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          opacity={0.2}
          cornerRadius={8}
        />
      </g>
    );
  };

  const handleLegendClick = (id) => {
    if (showGreyRing) return;
    setExpandedSliceId(prevId => (prevId === id ? null : id));
  };

  const handleLegendToggle = (legendKey) => {
    setActiveLegends(prev => ({
      ...prev,
      [legendKey]: !prev[legendKey]
    }));
    setExpandedSliceId(null);
  };

  const activeShapeIndex = chartData.findIndex(item => item.id === expandedSliceId);

  const getDisplayCityName = () => {
    if (!city) return "Fleet Workspace";
    if (cities && cities[city]) return cities[city];
    return String(city)
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${value.toLocaleString()}`;
  };

  // Determine color for breakeven days based on value
  const getBreakevenColor = (days) => {
    if (days <= 0) return "text-green-600";
    if (days <= 30) return "text-green-600";
    if (days <= 90) return "text-amber-600";
    return "text-red-600";
  };

  return (
    <div className="w-full bg-gradient-to-br from-white to-stone-50/80 border border-stone-200 rounded-2xl p-6 flex flex-col justify-start items-start gap-5 max-w-sm shadow-lg hover:shadow-xl transition-all duration-300 select-none backdrop-blur-sm">
      
      {/* Header Info Section with Chart Type Toggle */}
      <div className="w-full flex justify-between items-center">
        <div className="flex flex-col">
          <span className="text-stone-900 text-sm font-semibold font-['Outfit'] leading-5 bg-gradient-to-r from-stone-800 to-stone-600 bg-clip-text text-transparent">
            Vehicle Analytics
          </span>
          
        </div>
        
        {/* Chart Type Toggle */}
        <div className="flex bg-[#F0EFE9] p-1 rounded-xl gap-0.5">
          <button
            onClick={() => setChartType("breakdown")}
            className={`px-3 py-1 text-xs font-['Outfit'] rounded-lg transition-all ${
              chartType === "breakdown"
                ? "bg-white text-stone-900 font-semibold shadow-sm"
                : "text-stone-500 hover:text-stone-700"
            }`}
          >
            Breakdown
          </button>
          <button
            onClick={() => setChartType("utilization")}
            className={`px-3 py-1 text-xs font-['Outfit'] rounded-lg transition-all ${
              chartType === "utilization"
                ? "bg-white text-stone-900 font-semibold shadow-sm"
                : "text-stone-500 hover:text-stone-700"
            }`}
          >
            Utilization
          </button>
        </div>
      </div>

      {/* Segmented Time Slider Controls */}
      <div className="w-full flex justify-start items-center bg-[#F0EFE9] p-1 rounded-2xl gap-0.5 shadow-inner">
        {timeframes.map((tf) => {
          const isSelected = activeTimeframe === tf;
          return (
            <button
              key={tf}
              type="button"
              onClick={() => setActiveTimeframe(tf)}
              className={`flex-1 h-7 rounded-xl text-center text-xs font-['Outfit'] transition-all duration-200 outline-none ${
                isSelected 
                  ? "bg-white text-stone-900 font-semibold shadow-[0_2px_4px_rgba(0,0,0,0.1)] scale-[0.98]" 
                  : "text-stone-400 font-normal hover:text-stone-600 hover:bg-white/50"
              }`}
            >
              {tf}
            </button>
          );
        })}
      </div>

      {/* Stats Row - Utilization Rate & Breakeven Days */}
      <div className="w-full flex items-center justify-between gap-4 px-2 pt-1 border-b border-stone-100 pb-3">
        <div className="flex-1">
          <span className="text-stone-500 text-xs font-medium font-['Outfit'] uppercase tracking-wider">
            Utilization Rate
          </span>
          <span className="text-indigo-600 text-2xl font-bold font-['Outfit'] tracking-tight drop-shadow-sm block">
            {activeUtilizationRate}%
          </span>
        </div>
        <div className="flex-1 text-right">
          <span className="text-stone-500 text-xs font-medium font-['Outfit'] uppercase tracking-wider">
            Breakeven Days
          </span>
          <span className={`text-2xl font-bold font-['Outfit'] tracking-tight drop-shadow-sm block ${getBreakevenColor(breakEvenDays)}`}>
            {breakEvenDays}
          </span>
        </div>
      </div>

      {/* Chart Section - Conditional Rendering based on toggle */}
      <div className="w-full flex flex-col justify-center items-center mx-auto relative mt-2">
        
        {/* BREAKDOWN CHART - Horizontal Stacked Bar */}
        {chartType === "breakdown" && (
          <div className="w-full">
            <div className="w-full h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={breakdownData}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 60, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                  <XAxis 
                    type="number" 
                    tickFormatter={(value) => formatCurrency(value)}
                    stroke="#9CA3AF"
                    fontSize={10}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    stroke="#9CA3AF"
                    fontSize={11}
                    tickLine={false}
                    width={70}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="value" 
                    radius={[0, 8, 8, 0]}
                    barSize={40}
                  >
                    {breakdownData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.color}
                        opacity={entry.value === 0 ? 0.3 : 1}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Breakdown Summary */}
            <div className="w-full mt-3 p-3 bg-stone-50 rounded-xl">
              <div className="flex justify-between items-center mb-2">
                <span className="text-stone-500 text-xs font-medium">Total Revenue</span>
                <span className="text-stone-900 text-lg font-bold">{formatCurrency(revenue)}</span>
              </div>
              <div className="flex justify-between items-center text-xs text-stone-600">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-[#DC2626]" />
                  Fuel: {formatCurrency(fuelCost)}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-[#22C55E]" />
                  Maintenance: {formatCurrency(maintenance)}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-[#92400E]" />
                  Net Profit: {formatCurrency(netProfit)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* UTILIZATION CHART - Donut Chart */}
        {chartType === "utilization" && (
          <>
            <div className="w-[240px] h-[230px] relative flex items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-black/5 blur-md -z-10 translate-y-2 scale-95" />
              
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    activeIndex={activeShapeIndex !== -1 ? activeShapeIndex : undefined}
                    activeShape={renderActiveShape}
                    data={showGreyRing ? [{ id: 4, value: 100, name: "No Data Selected", color: "#D4D4D8" }] : chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={90}
                    paddingAngle={!showGreyRing && chartData.length > 1 ? 5 : 0}
                    dataKey="value"
                    cornerRadius={8}
                    onClick={(node) => handleLegendClick(node.id)}
                    animationDuration={600}
                    animationEasing="ease-out"
                  >
                    {(showGreyRing 
                      ? [{ id: 4, value: 100, name: "No Data Selected", color: "#D4D4D8" }]
                      : chartData
                    ).map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.color} 
                        stroke="none" 
                        className="outline-none transition-all duration-300 cursor-pointer hover:opacity-90"
                        style={{ filter: expandedSliceId === entry.id ? 'drop-shadow(0 4px 6px rgba(0,0,0,0.15))' : 'none' }}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              <div className="absolute flex flex-col items-center justify-center pointer-events-none text-center transform -translate-y-1">
                {selectedPercentage !== null ? (
                  <>
                    <span className="text-stone-400 text-[10px] font-medium font-['Outfit'] tracking-wider uppercase">
                      {selectedItem?.name}
                    </span>
                    <span className="text-stone-900 text-2xl font-bold font-['Outfit'] mt-0.5 tracking-tight">
                      {selectedPercentage}%
                    </span>
                  </>
                ) : (
                  <div className="w-8 h-8" />
                )}
              </div>
            </div>

            {/* Clickable Legends Grid */}
            <div className="w-full grid grid-cols-2 gap-x-6 gap-y-3.5 pl-4 mt-2">
              {[
                { id: 0, label: "Revenue", percentage: revenuePct, color: "bg-[#6559BC]", key: "revenue" },
                { id: 1, label: "Fuel Cost", percentage: fuelPct, color: "bg-[#DC2626]", key: "fuelCost" },
                { id: 2, label: "Maintenance", percentage: maintPct, color: "bg-[#22C55E]", key: "maintenance" },
                { id: 3, label: "Net Profit", percentage: profitPct, color: "bg-[#92400E]", key: "netProfit" }
              ].map((item) => {
                const isTargetSelected = expandedSliceId === item.id && !showGreyRing;
                const isActive = activeLegends[item.key];
                
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleLegendToggle(item.key)}
                    className={`flex items-center gap-2 justify-start transition-all duration-200 outline-none border rounded-lg p-1.5 ${
                      isTargetSelected 
                        ? "bg-gradient-to-r from-stone-100 to-white border-stone-200/80 scale-[1.02] shadow-md" 
                        : isActive
                        ? "border-transparent hover:bg-stone-50/60 hover:scale-[1.01] hover:border-stone-100"
                        : "border-stone-200 bg-stone-50/40 opacity-50 hover:opacity-70"
                    }`}
                  >
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${item.color} ${isTargetSelected ? 'ring-1 ring-offset-1 ring-stone-300' : ''}`} />
                    <span className={`text-xs font-medium font-['Outfit'] leading-tight truncate ${
                      isTargetSelected 
                        ? "text-stone-900 font-semibold" 
                        : isActive
                        ? "text-stone-600"
                        : "text-stone-400 line-through"
                    }`}>
                      {item.label} ({item.percentage}%)
                    </span>
                  </button>
                );
              })}
            </div>
            
            {showGreyRing && (
              <div className="w-full text-center mt-2">
                <span className="text-stone-400 text-[10px] font-['Outfit']">
                  Click a legend to view analytics
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}